import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Team → EOM (C-16 S2, GLE-8409): Command Center's EOM reconciliation in the
 * app. Every route is behind the backend switches `user_roles_enabled` and
 * `user_roles_eom_enabled`: while either is off it answers 404 with
 * error_code 1035, which the page reads as "not switched on".
 *
 * The month routes without a workspace are an Admin's (every workspace plus
 * the Glencoco catch-all); a workspace's month is an Admin's or that
 * workspace's LGA's. The server computes every figure; the page renders it.
 * Money is integer cents (rounded once per figure, C16-D361). Months are
 * "YYYY-MM" in ET.
 */

/** The catch-all bucket's key: meetings of every other workspace. */
export const EOM_CATCH_ALL_KEY = "glencoco";

export const EOM_REPORT_STATUSES = {
  NOT_STARTED: "not_started",
  DRAFT: "draft",
  LOCKED: "locked",
} as const;

export type EomReportStatusI =
  (typeof EOM_REPORT_STATUSES)[keyof typeof EOM_REPORT_STATUSES];

/** Why a meeting is (or isn't) in the month (C16-D353). */
export const EOM_MEETING_KINDS = {
  /** A successful charge this month. */
  CHARGED: "charged",
  /** Counted by an Admin's override, without a charge. */
  OVERRIDE: "override",
  /** Qualified this month, not charged (yet). */
  NOT_CHARGED: "not_charged",
  /** Qualified in an earlier month (from April 2026), never charged. */
  UNPAID_CARRYOVER: "unpaid_carryover",
  /** An unpaid carryover someone dismissed. */
  DISMISSED: "dismissed",
  /** Only a refund this month (charged in an earlier one). */
  REFUND_ONLY: "refund_only",
} as const;

export type EomMeetingKindI =
  (typeof EOM_MEETING_KINDS)[keyof typeof EOM_MEETING_KINDS];

export const EOM_LINE_ITEM_SECTIONS = {
  CREDIT: "credit",
  COGS: "cogs",
} as const;

export type EomLineItemSectionI =
  (typeof EOM_LINE_ITEM_SECTIONS)[keyof typeof EOM_LINE_ITEM_SECTIONS];

export const EOM_FUNDED_BY = {
  LGA: "lga",
  CUSTOMER: "customer",
  GLENCOCO: "glencoco",
} as const;

export type EomFundedByI = (typeof EOM_FUNDED_BY)[keyof typeof EOM_FUNDED_BY];

/** A bucket's month: meeting figures and the LGA payout. */
export interface EomFiguresI {
  qualified_meetings: number;
  gmv_cents: number;
  lga_bonus_cents: number;
  refunds_cents: number;
  /** What comes off the payout: the override plus LGA-funded rows. */
  rep_incentives_cents: number;
  /** Customer- and Glencoco-funded rows: shown, never deducted (C16-D307). */
  rep_incentives_tracked_cents: number;
  credits_cents: number;
  cogs_cents: number;
  /** Credits − COGS. */
  line_items_cents: number;
  /** LGA bonus − rep incentives + line items. */
  final_cents: number;
  not_charged: number;
  unpaid_carryover: number;
}

export interface EomBucketI {
  /** The workspace id, or EOM_CATCH_ALL_KEY. */
  workspace_key: string;
  workspace_id: string | null;
  name: string;
  status: EomReportStatusI;
  paid_status: string;
  /** False for the catch-all, which pays no LGA. */
  payable: boolean;
  figures: EomFiguresI;
}

export interface EomMonthResponseI {
  month: string;
  /** When charges were last read from Stripe; null = never. */
  stripe_synced_at: string | null;
  buckets: Array<EomBucketI>;
  total: EomFiguresI;
}

export interface EomReportI {
  id: string;
  status: EomReportStatusI;
  source: string;
  locked_at: string | null;
  paid_status: string;
  paid_at: string | null;
  lga_payout_proof_url: string | null;
  rep_incentives_override_cents: number | null;
}

export interface EomMeetingI {
  pms_id: string;
  activity_log_id: string;
  kind: EomMeetingKindI;
  counted: boolean;
  carryover: boolean;
  meeting_at: string;
  charged_at: string | null;
  payment_intent_id: string;
  campaign_id: string;
  campaign_name: string;
  rep_user_id: string;
  rep_name: string;
  account: string;
  contact: string;
  disputed: boolean;
  gmv_cents: number;
  rep_payout_cents: number;
  take_cents: number;
  lga_split_cents: number;
  lga_bonus_cents: number;
  refunds_cents: number;
}

export interface EomRepIncentiveI {
  id: string;
  source_incentive_id: string | null;
  is_auto: boolean;
  name: string;
  funded_by: EomFundedByI;
  /** LGA-funded: comes off the LGA payout. */
  deducted: boolean;
  rep_user_id: string | null;
  rep_email: string | null;
  rep_name: string | null;
  amount_cents: number;
  /** What it pays: the amount, or an excluded row's override (0 if none). */
  effective_cents: number;
  excluded: boolean;
  exclude_reason: string | null;
  override_amount_cents: number | null;
  notes: string | null;
  paid: boolean;
  paid_at: string | null;
  proof_url: string | null;
}

export interface EomLineItemI {
  id: string;
  section: EomLineItemSectionI;
  description: string;
  amount_cents: number;
  notes: string | null;
  attachment_url: string | null;
  evidence_url: string | null;
  item_date: string | null;
  paid: boolean;
  paid_at: string | null;
  proof_url: string | null;
  created_at: string;
}

export interface EomDirectIncentiveI {
  id: string;
  name: string;
  customer_name: string;
  customer_charge_cents: number;
  customer_invoiced: boolean;
  customer_invoiced_at: string | null;
  customer_proof_url: string | null;
  rep_name: string | null;
  rep_email: string | null;
  rep_payout_cents: number;
  rep_paid: boolean;
  rep_paid_at: string | null;
  rep_proof_url: string | null;
  margin_cents: number;
  notes: string | null;
}

export const EOM_AR_AP_KINDS = {
  /** A customer-funded direct incentive to bill (AR). */
  DIRECT_CUSTOMER: "direct_customer",
  /** A direct incentive's payout to its rep (AP). */
  DIRECT_REP: "direct_rep",
  REP_INCENTIVE: "rep_incentive",
  LGA_PAYOUT: "lga_payout",
  /** A negative final: the LGA owes Glencoco (AR). */
  LGA_OWES: "lga_owes",
} as const;

export interface EomArApRowI {
  /** One of EOM_AR_AP_KINDS. */
  kind: string;
  /** "ar" (owed to Glencoco) or "ap" (Glencoco owes). */
  direction: string;
  workspace_key: string;
  workspace_name: string;
  origin_id: string;
  description: string;
  counterparty: string;
  amount_cents: number;
  funded_by: string | null;
  paid: boolean;
  paid_at: string | null;
  proof_url: string | null;
}

export interface EomArApTotalsI {
  ar_cents: number;
  ar_paid_cents: number;
  ap_cents: number;
  ap_paid_cents: number;
}

/** One bucket's month in full. */
export interface EomBucketResponseI {
  month: string;
  stripe_synced_at: string | null;
  bucket: EomBucketI;
  /** Null until someone writes to the month. */
  report: EomReportI | null;
  meetings: Array<EomMeetingI>;
  rep_incentives: Array<EomRepIncentiveI>;
  line_items: Array<EomLineItemI>;
  directs: Array<EomDirectIncentiveI>;
  ar_ap: Array<EomArApRowI>;
  ar_ap_totals: EomArApTotalsI;
}

/** Every bucket's AR/AP (Admin). */
export interface EomArApResponseI {
  month: string;
  rows: Array<EomArApRowI>;
  totals: EomArApTotalsI;
}

/** One month of QM Contribution (C16-D358); CC basis next to it (D301). */
export interface EomContributionMonthI {
  month: string;
  qualified_meetings: number;
  gmv_cents: number;
  refunds_cents: number;
  net_gmv_cents: number;
  rep_payouts_cents: number;
  lga_splits_cents: number;
  gross_margin_cents: number;
  lga_bonus_cents: number;
  rep_incentives_deducted_cents: number;
  credits_cents: number;
  cogs_cents: number;
  net_lga_settlement_cents: number;
  rep_incentives_paid_cents: number;
  direct_charges_cents: number;
  direct_payouts_cents: number;
  direct_margin_cents: number;
  margin_cents: number;
  cc_rep_incentives_cents: number;
  cc_net_lga_settlement_cents: number;
  cc_basis_margin_cents: number;
  stripe_charges_all_cents: number;
  unreconciled_cents: number;
}

export interface EomContributionResponseI {
  from: string;
  to: string;
  stripe_synced_at: string | null;
  months: Array<EomContributionMonthI>;
}

export const EOM_STRIPE_STATUSES = {
  SUCCEEDED: "succeeded",
  PENDING: "pending",
  FAILED: "failed",
} as const;

export interface EomStripeSyncRunI {
  id: string;
  /** running, succeeded or failed. */
  status: string;
  since: string;
  started_at: string;
  finished_at: string | null;
  charges_read: number;
  refunds_read: number;
  error: string | null;
}

export interface EomStripeStatusResponseI {
  /** The last successful sync's end; null = never. */
  synced_at: string | null;
  latest: EomStripeSyncRunI | null;
}

export interface EomStripeChargeI {
  charge_id: string;
  payment_intent_id: string;
  customer_id: string;
  description: string;
  amount_cents: number;
  amount_refunded_cents: number;
  currency: string;
  status: string;
  payment_method_type: string;
  failure_message: string;
  attempted_at: string;
  succeeded_at: string | null;
  disputed: boolean;
  /** A rep was paid on this charge's meeting, but the charge didn't succeed. */
  transfer_without_payment: boolean;
}

export interface EomStripeChargesResponseI {
  charges: Array<EomStripeChargeI>;
  total: number;
  page: number;
  page_size: number;
}

/** The Stripe tab's filters; dates "YYYY-MM-DD" (ET), `to` inclusive. */
export interface EomStripeChargesQueryI {
  search: string;
  status: string;
  from: string;
  to: string;
  page: number;
}

/** A customer-funded (direct) incentive, created or replaced (Admin). */
export interface EomDirectInputI {
  name: string;
  period_start: string | null;
  period_end: string | null;
  customer_name: string;
  customer_charge_cents: number;
  rep_email: string | null;
  rep_name: string | null;
  rep_payout_cents: number;
  notes: string | null;
}

/** Which side of a direct incentive a tick is for. */
export type EomDirectSideI = "customer" | "rep";

export const EOM_ERROR_CODES = {
  BAD_MONTH: 1290,
  NOT_A_BUCKET: 1291,
  SYNC_RUNNING: 1292,
  STRIPE_UNAVAILABLE: 1293,
  MONTH_OPEN: 1294,
  LOCKED: 1295,
  ROW_NOT_FOUND: 1296,
  NOT_PAYABLE: 1297,
  CARRIED_MONTH: 1298,
  /** The month changed while it was being locked: check it, lock again. */
  CHANGED: 1299,
  /** A blocking check isn't acknowledged: look at the checks, then lock. */
  CHECKS_OPEN: 1363,
  /** An Admin's change without the name of the person making it. */
  SIGNATURE_REQUIRED: 1364,
} as const;

/** One field a change moved; money in fields ending _cents. */
export interface EomHistoryChangeI {
  field: string;
  before: string | null;
  after: string | null;
}

/** One change to a workspace month (GLE-8527). */
export interface EomHistoryEntryI {
  id: string;
  action: string;
  entity: string;
  entity_id: string | null;
  /** What it was about: a line, an incentive and its rep, a refresh. */
  subject: string | null;
  at: string;
  /** The account that made it (its name). */
  actor_name: string | null;
  /** The person who signed it (an Admin at the shared account). */
  signed_by: string | null;
  /** Why: an unlock's reason, a lock's note. */
  note: string | null;
  /** The blocking checks a lock was signed past. */
  acknowledged: Array<string>;
  changes: Array<EomHistoryChangeI>;
}

/** Check severities: block (acknowledge to lock), warn, info. */
export const EOM_CHECK_SEVERITIES = {
  BLOCK: "block",
  WARN: "warn",
  INFO: "info",
} as const;

/** Check keys (append-only on the server; unknown ones are shown as sent). */
export const EOM_CHECK_KEYS = {
  COUNTED_IN_LOCKED_MONTH: "counted_in_locked_month",
  STRIPE_NOT_READ: "stripe_not_read_since_month_end",
  CHARGES_PENDING: "charges_pending",
  DISPUTED_COUNTED: "disputed_counted",
  REFUNDS_ON_EARLIER: "refunds_on_earlier_months",
  INCENTIVES_NOT_LGA: "incentives_not_lga_funded",
  LINES_WITHOUT_EVIDENCE: "lines_without_evidence",
  DUPLICATE_LINES: "duplicate_lines",
  UNPAID_CARRYOVER: "unpaid_carryover",
  COUNTED_BY_HAND: "counted_by_hand",
  NOT_CHARGED: "not_charged",
} as const;

/** One "before you lock" finding on a workspace month (GLE-8527). */
export interface EomCheckI {
  key: string;
  severity: string;
  count: number;
  /** The money at stake: a bonus, GMV, a charge, a refund or a row amount. */
  amount_cents: number;
  /** The meetings, charges, lines or incentive rows it found (up to 50). */
  ids: Array<string>;
  /** The other locked months involved (counted_in_locked_month). */
  months: Array<string>;
  /** When Stripe was last read (stripe_not_read_since_month_end). */
  at: string | null;
}

export interface EomBucketChecksI {
  workspace_key: string;
  name: string;
  locked: boolean;
  checks: Array<EomCheckI>;
}

export interface EomChecksResponseI {
  month: string;
  stripe_synced_at: string | null;
  buckets: Array<EomBucketChecksI>;
}

export interface EomHistoryResponseI {
  month: string;
  entries: Array<EomHistoryEntryI>;
  /** More than 500 changes: the oldest are left out. */
  truncated: boolean;
}

/** A credit or COGS line (create or replace). */
export interface EomLineItemInputI {
  section: EomLineItemSectionI;
  description: string;
  amount_cents: number;
  notes: string | null;
  attachment_url: string | null;
  evidence_url: string | null;
  /** "YYYY-MM-DD". */
  item_date: string | null;
}

/** Excludes a rep incentive (it still pays the override, if any) or not. */
export interface EomExclusionInputI {
  excluded: boolean;
  reason: string | null;
  override_amount_cents: number | null;
}

/** Ticked paid (or not), with the proof link. */
export interface EomPaymentInputI {
  paid: boolean;
  proof_url: string | null;
}

/** A manual rep incentive row (Admin). */
export interface EomRepIncentiveInputI {
  name: string;
  funded_by: EomFundedByI;
  rep_email: string | null;
  rep_name: string | null;
  amount_cents: number;
  notes: string | null;
}

/** What refreshing rep incentives from Team → Incentives did. */
export interface EomRefreshResponseI {
  inserted: number;
  updated: number;
  removed: number;
  /** Rows already paid whose payout changed: they keep what was paid. */
  kept_paid: number;
}

/**
 * Who signs a write (GLE-8527): an Admin's write carries the person's name,
 * because finance shares the Admin account; an LGA's carries nothing (the
 * server ignores it).
 */
export interface EomSignI {
  signed_by?: string;
}

/** Locks a month: an optional note, and the blocking checks acknowledged. */
export interface EomLockInputI {
  note?: string | null;
  acknowledged?: Array<string>;
}

export interface EomWriteResponseI {
  id: string;
}

/**
 * A 404 (+ 1035) means "switched off": an answer the page reads, not an
 * error to log. A fresh object per request, because the meta interceptor
 * writes into `meta` (as manage.ts does).
 */
const switchProbe = (
  config: AxiosRequestConfig = {}
): AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI => ({
  ...config,
  meta: { expectedStatuses: [404] },
});

/**
 * A write's config (GLE-8527): its body carries the name of the person
 * signing it, and notes, so a failure is logged without the body. A fresh
 * object per request, like switchProbe.
 */
const signedWrite = (
  config: AxiosRequestConfig = {}
): AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI => ({
  ...config,
  meta: { redactBody: true },
});

const monthPath = (month: string) =>
  `/v1/manage/eom/months/${encodeURIComponent(month)}`;

const workspaceMonthPath = (workspaceId: string, month: string) =>
  `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/eom/months/${encodeURIComponent(month)}`;

const bucketPath = (month: string, bucket: string) =>
  `${monthPath(month)}/buckets/${encodeURIComponent(bucket)}`;

export const ManageEomAPIEndpoints = (api: AxiosInstance) => ({
  /** Every bucket's month and the total (Admin). */
  getMonth: (month: string) =>
    api.get<EomMonthResponseI>(monthPath(month), switchProbe()),

  /** One workspace's month in full (Admin, or the workspace's LGA). */
  getWorkspaceMonth: (workspaceId: string, month: string) =>
    api.get<EomBucketResponseI>(
      workspaceMonthPath(workspaceId, month),
      switchProbe()
    ),

  /** The Glencoco catch-all's month in full (Admin). */
  getUnassigned: (month: string) =>
    api.get<EomBucketResponseI>(
      `${monthPath(month)}/unassigned`,
      switchProbe()
    ),

  /** Every bucket's AR/AP for the month (Admin). */
  getArAp: (month: string) =>
    api.get<EomArApResponseI>(`${monthPath(month)}/ar_ap`, switchProbe()),

  /** QM Contribution for [from, to] (Admin, at most 24 months). */
  getContribution: (from: string, to: string) =>
    api.get<EomContributionResponseI>(
      "/v1/manage/eom/contribution",
      switchProbe({ params: { from, to } })
    ),

  getStripeStatus: () =>
    api.get<EomStripeStatusResponseI>(
      "/v1/manage/eom/stripe/status",
      switchProbe()
    ),

  getStripeCharges: ({
    search,
    status,
    from,
    to,
    page,
  }: EomStripeChargesQueryI) =>
    api.get<EomStripeChargesResponseI>(
      "/v1/manage/eom/stripe/charges",
      switchProbe({
        params: {
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          from,
          to,
          page,
        },
      })
    ),

  /** Every LGA workspace's checks before locking the month (Admin). */
  getChecks: (month: string) =>
    api.get<EomChecksResponseI>(`${monthPath(month)}/checks`, switchProbe()),

  /** A workspace month's history (its LGA or an Admin). */
  getWorkspaceHistory: (workspaceId: string, month: string) =>
    api.get<EomHistoryResponseI>(
      `${workspaceMonthPath(workspaceId, month)}/history`,
      switchProbe()
    ),

  /** A bucket month's history, the catch-all's included (Admin). */
  getBucketHistory: (month: string, bucket: string) =>
    api.get<EomHistoryResponseI>(
      `${bucketPath(month, bucket)}/history`,
      switchProbe()
    ),

  /** Reads charges and refunds from Stripe (reads only; no money moves). */
  syncStripe: () =>
    api.post<{ run: EomStripeSyncRunI }>("/v1/manage/eom/stripe/sync", {}),

  // Writes (C16-D351). A workspace's LGA (or an Admin) edits a draft month
  // through the workspace's path; the rest is an Admin's, by bucket. Every
  // write is audited, and refused while impersonating.

  createLineItem: (
    workspaceId: string,
    month: string,
    body: EomLineItemInputI,
    sign: EomSignI = {}
  ) =>
    api.post<EomWriteResponseI>(
      `${workspaceMonthPath(workspaceId, month)}/line_items`,
      { ...body, ...sign },
      signedWrite()
    ),

  updateLineItem: (
    workspaceId: string,
    month: string,
    itemId: string,
    body: EomLineItemInputI,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${workspaceMonthPath(workspaceId, month)}/line_items/${encodeURIComponent(itemId)}`,
      { ...body, ...sign },
      signedWrite()
    ),

  deleteLineItem: (
    workspaceId: string,
    month: string,
    itemId: string,
    sign: EomSignI = {}
  ) =>
    api.delete<EomWriteResponseI>(
      `${workspaceMonthPath(workspaceId, month)}/line_items/${encodeURIComponent(itemId)}`,
      signedWrite({ data: sign })
    ),

  setExclusion: (
    workspaceId: string,
    month: string,
    itemId: string,
    body: EomExclusionInputI,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${workspaceMonthPath(workspaceId, month)}/rep_incentives/${encodeURIComponent(itemId)}/exclusion`,
      { ...body, ...sign },
      signedWrite()
    ),

  setCarryoverDismissed: (
    workspaceId: string,
    month: string,
    pmsId: string,
    dismissed: boolean,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${workspaceMonthPath(workspaceId, month)}/meetings/${encodeURIComponent(pmsId)}/carryover`,
      { dismissed, ...sign },
      signedWrite()
    ),

  lock: (
    month: string,
    bucket: string,
    body: EomLockInputI = {},
    sign: EomSignI = {}
  ) =>
    api.post<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/lock`,
      {
        ...body,
        ...sign,
      },
      signedWrite()
    ),

  /** Reopens a locked month; the reason is required. */
  unlock: (
    month: string,
    bucket: string,
    reason: string,
    sign: EomSignI = {}
  ) =>
    api.post<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/unlock`,
      {
        reason,
        ...sign,
      },
      signedWrite()
    ),

  setPayoutPaid: (
    month: string,
    bucket: string,
    body: EomPaymentInputI,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/payout`,
      {
        ...body,
        ...sign,
      },
      signedWrite()
    ),

  /** The extra amount taken off the payout; null clears it. */
  setOverride: (
    month: string,
    bucket: string,
    amountCents: number | null,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/override`,
      {
        amount_cents: amountCents,
        ...sign,
      },
      signedWrite()
    ),

  refreshIncentives: (month: string, bucket: string, sign: EomSignI = {}) =>
    api.post<EomRefreshResponseI>(
      `${bucketPath(month, bucket)}/refresh_incentives`,
      sign,
      signedWrite()
    ),

  /**
   * A meeting's "counted" override: true counts it in its meeting month
   * without a charge, false never counts it, null goes back to the rule.
   */
  setCounted: (
    month: string,
    bucket: string,
    pmsId: string,
    override: boolean | null,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/meetings/${encodeURIComponent(pmsId)}/counted`,
      { month, override, ...sign },
      signedWrite()
    ),

  createRepIncentive: (
    month: string,
    bucket: string,
    body: EomRepIncentiveInputI,
    sign: EomSignI = {}
  ) =>
    api.post<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/rep_incentives`,
      {
        ...body,
        ...sign,
      },
      signedWrite()
    ),

  updateRepIncentive: (
    month: string,
    bucket: string,
    itemId: string,
    body: EomRepIncentiveInputI,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/rep_incentives/${encodeURIComponent(itemId)}`,
      { ...body, ...sign },
      signedWrite()
    ),

  deleteRepIncentive: (
    month: string,
    bucket: string,
    itemId: string,
    sign: EomSignI = {}
  ) =>
    api.delete<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/rep_incentives/${encodeURIComponent(itemId)}`,
      signedWrite({ data: sign })
    ),

  setRepIncentivePaid: (
    month: string,
    bucket: string,
    itemId: string,
    body: EomPaymentInputI,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/rep_incentives/${encodeURIComponent(itemId)}/payment`,
      { ...body, ...sign },
      signedWrite()
    ),

  createDirect: (
    month: string,
    bucket: string,
    body: EomDirectInputI,
    sign: EomSignI = {}
  ) =>
    api.post<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/directs`,
      {
        ...body,
        ...sign,
      },
      signedWrite()
    ),

  updateDirect: (
    month: string,
    bucket: string,
    itemId: string,
    body: EomDirectInputI,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/directs/${encodeURIComponent(itemId)}`,
      { ...body, ...sign },
      signedWrite()
    ),

  deleteDirect: (
    month: string,
    bucket: string,
    itemId: string,
    sign: EomSignI = {}
  ) =>
    api.delete<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/directs/${encodeURIComponent(itemId)}`,
      signedWrite({ data: sign })
    ),

  /** Ticks a direct incentive's customer invoiced, or its rep paid. */
  setDirectPaid: (
    month: string,
    bucket: string,
    itemId: string,
    side: EomDirectSideI,
    body: EomPaymentInputI,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/directs/${encodeURIComponent(itemId)}/payment`,
      { side, ...body, ...sign },
      signedWrite()
    ),

  setLineItemPaid: (
    month: string,
    bucket: string,
    itemId: string,
    body: EomPaymentInputI,
    sign: EomSignI = {}
  ) =>
    api.put<EomWriteResponseI>(
      `${bucketPath(month, bucket)}/line_items/${encodeURIComponent(itemId)}/payment`,
      { ...body, ...sign },
      signedWrite()
    ),
});
